import "twin.macro";

import { PlusIcon, RefreshCcwIcon, Trash } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useBoolean } from "usehooks-ts";

import { useDeleteUser } from "@/api/users/mutations";
import { useUsersSuspense } from "@/api/users/queries";
import { Auth0User } from "@/api/users/types";
import { Button } from "@/components/buttons";
import { DeleteConfirmationModal } from "@/components/modals/Modals";
import { PageContent } from "@/components/page";
import { useClientContext } from "@/data-loaders/client-data-loader";

import { CreateUserModalForm } from "./CreateUserModale";

export interface UserUpdateValues {
  firstname: string;
  lastname: string;
  email: string;
  id: string;
}

export const UsersLayout = () => {
  const { t } = useTranslation();
  const [openUserModal, setOpenUserModal] = useState(false);

  const {
    value: isDeleteUserOpen,
    setTrue: openDeleteUser,
    setFalse: closeDeleteUser,
  } = useBoolean(false);

  const client = useClientContext();
  const { data: users, refetch } = useUsersSuspense({ id: client._id });

  const deleteUser = useDeleteUser(client._id);
  const onDelete = async (userId: string) => {
    try {
      deleteUser.mutateAsync(userId);
    } catch (error) {
      console.error(error);
    }
  };

  const [userIdToDelete, setUserIdToDelete] = useState<string | null>(null);

  const handleDelete = (userId: string) => {
    setUserIdToDelete(userId);
    openDeleteUser();
  };

  const confirmDelete = async () => {
    if (userIdToDelete) {
      await onDelete(userIdToDelete);
      setUserIdToDelete(null);
      closeDeleteUser();
    }
  };

  return (
    <PageContent tw="max-w-7xl">
      <div tw="flex flex-col gap-4 z-10">
        <div tw="flex items-center justify-end gap-2">
          <div
            tw="transition flex items-center justify-center rounded-xl px-4 py-2
            text-center text-sm font-semibold border bg-white border-gray-300 text-gray-700 hover:bg-gray-100 focus:(ring-gray-500) focus:(ring-2 ring-offset-2 outline-none) disabled:(opacity-70 cursor-not-allowed) cursor-pointer"
            onClick={() => refetch()}
          >
            <RefreshCcwIcon tw="h-5 w-5 text-gray-400" />
          </div>
          <Button secondary onClick={() => setOpenUserModal(true)} tw="shadow">
            {" "}
            <PlusIcon tw="w-5 h-5 mr-2" />
            {t("form.addUser.button")}
          </Button>
        </div>

        {users && users.data.length > 0 ? (
          <table tw="rounded-xl shadow-sm min-w-full divide-y divide-gray-300 bg-white overflow-hidden">
            <thead tw="bg-primary-800  font-bold">
              <tr>
                <th
                  scope="col"
                  tw="px-3 py-3.5 text-left text-sm font-semibold text-white"
                >
                  Nom
                </th>
                <th
                  scope="col"
                  tw="px-3 py-3.5 text-left text-sm font-semibold text-white"
                >
                  Email
                </th>
                <th scope="col" tw="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span tw="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody tw="divide-y divide-gray-200 bg-white">
              {users?.data.map((user, index) => {
                return (
                  <UserCard key={index} user={user} onDelete={handleDelete} />
                );
              })}
            </tbody>
          </table>
        ) : (
          <div tw="flex items-center justify-center ">
            <p tw="text-lg text-gray-500">{t("users.noUser")}</p>
          </div>
        )}
      </div>

      <CreateUserModalForm
        open={openUserModal}
        setOpen={setOpenUserModal}
        client={client}
      />
      <DeleteConfirmationModal
        onConfirm={confirmDelete}
        open={isDeleteUserOpen}
        onClose={closeDeleteUser}
        title={t("users.delete.title")}
        content={t("users.delete.content")}
        confirmText={t("users.delete.confirm")}
        cancelText={t("users.delete.cancel")}
      />
    </PageContent>
  );
};

interface UserCardProps {
  user: Auth0User;
  onDelete: (userId: string) => void;
}

const UserCard = ({ user, onDelete }: UserCardProps) => {
  return (
    <tr key={user.email} tw="bg-white">
      <td tw="whitespace-nowrap px-3 py-4 text-sm font-bold text-gray-600">
        {user?.family_name?.toUpperCase()} {user?.given_name}
      </td>
      <td tw="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
        {user.email}
      </td>
      <td tw="flex flex-1 justify-end text-gray-600 hover:text-gray-900 px-3 py-4 cursor-pointer">
        <Trash
          tw="w-5 h-5 text-primary-950 cursor-pointer"
          onClick={() => onDelete(user.user_id)}
        />
      </td>
    </tr>
  );
};
